import api from './api';
import axios from 'axios';
import { DataQuery, DeviceRef, MeasurementRecord, MetricDef, PagedResult, SeriesResult, SummaryResult, DevicesDirectory, DeviceSummaryItem, Asset, Customer, AppUser, SeriesPoint } from '../types';
import type { PivotedMeasurementRecord } from '../types/pivoted';

// Local fallback metric IDs (used only if backend is unreachable)
const METRICS = [
  'temperature',
  'humidity.general',
  'battery',
];

// Weather extras
export type WeatherExtraMetric = 'weather.temperature' | 'weather.humidity' | 'weather.precipitation';
const WEATHER_UNITS: Record<WeatherExtraMetric, string> = {
  'weather.temperature': '°C',
  'weather.humidity': '%',
  'weather.precipitation': 'mm',
};

// Centralized test mode flag: prefer localStorage, fallback to URL param ?test=true
const TEST_STORAGE_KEY = 'tt.testMode';
export function isTestMode(): boolean {
  try {
    const v = localStorage.getItem(TEST_STORAGE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
  } catch {
    // ignore
  }
  try {
    return new URLSearchParams(window.location.search).get('test') === 'true';
  } catch {
    return false;
  }
}

// ---------------------------------------------
// Assets, Customers, Users (Directory-like APIs)
// ---------------------------------------------

function paginateArray<T>(items: T[], page = 1, pageSize = 10): PagedResult<T> {
  const start = (page - 1) * pageSize;
  const paged = items.slice(start, start + pageSize);
  return { items: paged, total: items.length, page, pageSize };
}

function buildDummyCustomers(): Customer[] {
  const names = ['Acme AG', 'Nordholz GmbH', 'TimberWorks KG', 'Wald & Co.', 'Holzmeister AG', 'Bayern Timber', 'BlackForest GmbH', 'Alpenholz AG', 'Rheinholz GmbH', 'Elbholz KG'];
  return names.map((name, i) => ({ id: `cust-${i + 1}`, name, email: `contact${i + 1}@example.com`, phone: `+49 89 1234${String(i).padStart(3, '0')}` }));
}

function buildDummyUsers(): AppUser[] {
  const roles: AppUser['role'][] = ['admin', 'user', 'viewer'];
  return Array.from({ length: 12 }, (_, i) => ({
    id: `usr-${i + 1}`,
    name: `User ${i + 1}`,
    email: `user${i + 1}@example.com`,
    role: roles[i % roles.length],
  }));
}

function buildDummyAssets(customers: Customer[]): Asset[] {
  const types = ['gateway', 'sensor', 'machine'];
  const locs = ['Munich', 'Berlin', 'Hamburg', 'Stuttgart', 'Cologne', 'Leipzig'];
  return Array.from({ length: 30 }, (_, i) => ({
    id: `as-${i + 1}`,
    name: `Asset ${i + 1}`,
    type: types[i % types.length],
    customerId: customers[i % customers.length].id,
    location: locs[i % locs.length],
  }));
}

// Assets
export async function listAssets(params?: { q?: string; page?: number; pageSize?: number; customerId?: string }): Promise<PagedResult<Asset>> {
  if (isTestMode()) {
    const customers = buildDummyCustomers();
    let items = buildDummyAssets(customers);
    const q = (params?.q || '').toLowerCase();
    if (q) items = items.filter((a) => a.id.toLowerCase().includes(q) || a.name.toLowerCase().includes(q) || (a.type || '').toLowerCase().includes(q));
    if (params?.customerId) items = items.filter((a) => a.customerId === params.customerId);
    return paginateArray(items, params?.page || 1, params?.pageSize || 10);
  }
  const resp = await axios.get(`${API_BASE}/assets`, { params });
  return resp.data as PagedResult<Asset>;
}

export async function upsertAsset(payload: Partial<Asset>): Promise<{ success: true; id?: string }> {
  if (isTestMode()) {
    // In test mode we just acknowledge success and echo/generate id
    const id = payload.id || `as-${Math.floor(Math.random() * 100000)}`;
    return { success: true, id };
  }
  await axios.post(`${API_BASE}/assets`, payload);
  return { success: true, id: payload.id };
}

// Customers
export async function listCustomers(params?: { q?: string; page?: number; pageSize?: number }): Promise<PagedResult<Customer>> {
  if (isTestMode()) {
    let items = buildDummyCustomers();
    const q = (params?.q || '').toLowerCase();
    if (q) items = items.filter((c) => c.id.toLowerCase().includes(q) || c.name.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
    return paginateArray(items, params?.page || 1, params?.pageSize || 10);
  }
  const resp = await axios.get(`${API_BASE}/customers`, { params });
  return resp.data as PagedResult<Customer>;
}

export async function upsertCustomer(payload: Partial<Customer>): Promise<{ success: true; id?: string }> {
  if (isTestMode()) {
    const id = payload.id || `cust-${Math.floor(Math.random() * 100000)}`;
    return { success: true, id };
  }
  await axios.post(`${API_BASE}/customers`, payload);
  return { success: true, id: payload.id };
}

// Users
export async function listUsers(params?: { q?: string; page?: number; pageSize?: number; role?: string }): Promise<PagedResult<AppUser>> {
  if (isTestMode()) {
    let items = buildDummyUsers();
    const q = (params?.q || '').toLowerCase();
    if (q) items = items.filter((u) => u.id.toLowerCase().includes(q) || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    if (params?.role) items = items.filter((u) => String(u.role) === String(params.role));
    return paginateArray(items, params?.page || 1, params?.pageSize || 10);
  }
  const resp = await api.get(`${API_BASE}/users`, { params });
  return resp.data as PagedResult<AppUser>;
}

export async function upsertUser(payload: Partial<AppUser>): Promise<{ success: true; id?: string }> {
  if (isTestMode()) {
    const id = payload.id || `usr-${Math.floor(Math.random() * 100000)}`;
    return { success: true, id };
  }
  await api.post(`${API_BASE}/users`, payload);
  return { success: true, id: payload.id };
}


function randomBetween(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

// Open-Meteo Geocoding (no API key required)
export interface GeoPlace {
  name: string;
  country?: string;
  admin1?: string;
  latitude: number;
  longitude: number;
}

type GeocodeApiResult = {
  name: string;
  country?: string;
  admin1?: string;
  latitude: number;
  longitude: number;
};

export async function geocodeLocations(q: string, limit = 8): Promise<GeoPlace[]> {
  if (!q || !q.trim()) return [];
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=${limit}&language=de&format=json`;
  const resp = await fetch(url);
  if (!resp.ok) return [];
  const data = await resp.json();
  const results = Array.isArray(data?.results) ? data.results : [];
  return results.map((r: GeocodeApiResult) => ({
    name: String(r.name),
    country: r.country ? String(r.country) : undefined,
    admin1: r.admin1 ? String(r.admin1) : undefined,
    latitude: Number(r.latitude),
    longitude: Number(r.longitude),
  }));
}

// City/Address autocomplete via OpenStreetMap Nominatim (no API key required)
// Note: Please respect Nominatim usage policy if used in production.
export type NominatimPlace = {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
};

export async function geocodeCityNominatim(q: string, limit = 8): Promise<Array<{ label: string; name: string; country?: string; postcode?: string }>> {
  if (!q || !q.trim()) return [];
  const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=${limit}&q=${encodeURIComponent(q)}`;
  const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!resp.ok) return [];
  const data = (await resp.json()) as NominatimPlace[];
  const out: Array<{ label: string; name: string; country?: string; postcode?: string }> = [];
  for (const p of data) {
    const addr = p.address || {};
    const cityLike = addr.city || addr.town || addr.village;
    if (!cityLike) continue;
    const label = `${cityLike}${addr.country ? ', ' + addr.country : ''}${addr.postcode ? ' (' + addr.postcode + ')' : ''}`;
    out.push({ label, name: cityLike, country: addr.country, postcode: addr.postcode });
  }
  // Dedupe by label
  return Array.from(new Map(out.map(o => [o.label, o])).values());
}

// Fetch weather time series from Open-Meteo Archive API and map to SeriesPoint[]
export async function fetchWeatherSeries(params: {
  latitude: number;
  longitude: number;
  from?: string;
  to?: string;
  interval?: string; // e.g. '30m','1h','24h','1mo' (we will map to hourly)
  metrics: Array<'weather.temperature' | 'weather.humidity' | 'weather.precipitation'>;
}): Promise<{ items: SeriesPoint[]; units: Record<string, string> }> {
  const { latitude, longitude, from, to } = params;

  // Local helper (avoid dependency on declaration order of toDateOnly below)
  const toDateOnlyLocal = (iso?: string) => {
    if (!iso) return undefined;
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return undefined;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Decide granularity based on selected interval
  // Use daily aggregation for coarse intervals to reduce data volume
  const wantDaily = (() => {
    const iv = params.interval || '';
    if (!iv) return false;
    if (iv.endsWith('mo')) return true; // monthly
    if (iv.endsWith('h')) {
      const n = parseInt(iv.replace('h', '')) || 0;
      return n >= 24; // 24h and above
    }
    if (iv.endsWith('d')) return true; // explicit days
    return false;
  })();

  // Build time range (date-only for API)
  const start = toDateOnlyLocal(from);
  const end = toDateOnlyLocal(to);

  // Decide which API to use:
  // Forecast API covers up to 92 past days + future; archive API covers older data.
  // If the range end is within the last 92 days, use forecast API; otherwise archive API.
  const nowMs = Date.now();
  const endMs = end ? new Date(end).getTime() : nowMs;
  const useForcast = (nowMs - endMs) < 92 * 24 * 60 * 60 * 1000;
  const apiBase = useForcast
    ? 'https://api.open-meteo.com/v1/forecast'
    : 'https://archive-api.open-meteo.com/v1/era5';

  const url = new URL(apiBase);
  url.searchParams.set('latitude', String(latitude));
  url.searchParams.set('longitude', String(longitude));
  if (start) url.searchParams.set('start_date', start);
  if (end) url.searchParams.set('end_date', end);
  url.searchParams.set('timezone', 'auto');

  if (wantDaily) {
    // Daily variables: use mean for temp/humidity, sum for precipitation
    const dailyParams: string[] = [];
    if (params.metrics.includes('weather.temperature')) dailyParams.push(useForcast ? 'temperature_2m_max' : 'temperature_2m_mean');
    if (params.metrics.includes('weather.humidity')) dailyParams.push(useForcast ? 'relative_humidity_2m_max' : 'relative_humidity_2m_mean');
    if (params.metrics.includes('weather.precipitation')) dailyParams.push('precipitation_sum');
    if (dailyParams.length) url.searchParams.set('daily', dailyParams.join(','));
  } else {
    // Hourly variables
    const hourlyParams: string[] = [];
    if (params.metrics.includes('weather.temperature')) hourlyParams.push('temperature_2m');
    if (params.metrics.includes('weather.humidity')) hourlyParams.push('relative_humidity_2m');
    if (params.metrics.includes('weather.precipitation')) hourlyParams.push('precipitation');
    if (hourlyParams.length) url.searchParams.set('hourly', hourlyParams.join(','));
  }

  const resp = await fetch(url.toString());
  if (!resp.ok) {
    return { items: [], units: {} };
  }
  const json = await resp.json();
  // Open-Meteo can return HTTP 200 with an error field on invalid ranges
  if (json?.error) {
    return { items: [], units: {} };
  }

  // Map response to SeriesPoint[] with OHLC objects (o==h==l==c)
  const rows: { timestamp: string }[] = [];
  let tArr: number[] | undefined;
  let rhArr: number[] | undefined;
  let prArr: number[] | undefined;

  if (wantDaily && Array.isArray(json?.daily?.time)) {
    for (const ts of json.daily.time as string[]) rows.push({ timestamp: ts });
    // Forecast API uses _max, archive API uses _mean — pick whichever is present
    tArr = (json?.daily?.temperature_2m_mean ?? json?.daily?.temperature_2m_max) as number[] | undefined;
    rhArr = (json?.daily?.relative_humidity_2m_mean ?? json?.daily?.relative_humidity_2m_max) as number[] | undefined;
    prArr = json?.daily?.precipitation_sum as number[] | undefined;
  } else if (Array.isArray(json?.hourly?.time)) {
    // Normalize to UTC ISO so forward-fill aligns with sensor timestamps
    for (const ts of json.hourly.time as string[]) {
      const ms = Date.parse(ts.replace(' ', 'T'));
      rows.push({ timestamp: Number.isFinite(ms) ? new Date(ms).toISOString() : ts });
    }
    tArr = json?.hourly?.temperature_2m as number[] | undefined;
    rhArr = json?.hourly?.relative_humidity_2m as number[] | undefined;
    prArr = json?.hourly?.precipitation as number[] | undefined;
  }

  const byTs = new Map<string, SeriesPoint>(rows.map((r) => [r.timestamp, { timestamp: r.timestamp } as SeriesPoint]));
  const putMetric = (label: WeatherExtraMetric, idx: number, arr?: number[]) => {
    if (!arr || idx >= arr.length) return;
    const v = Number(arr[idx]);
    if (!Number.isFinite(v)) return;
    const row = byTs.get(rows[idx].timestamp);
    if (row) (row as Record<string, SeriesPoint[string]>)[label] = { low: v, q1: v, median: v, q3: v, high: v } as SeriesPoint[string];
  };

  for (let i = 0; i < rows.length; i++) {
    if (params.metrics.includes('weather.temperature')) putMetric('weather.temperature', i, tArr);
    if (params.metrics.includes('weather.humidity')) putMetric('weather.humidity', i, rhArr);
    if (params.metrics.includes('weather.precipitation')) putMetric('weather.precipitation', i, prArr);
  }

  const units: Record<string, string> = {};
  for (const m of params.metrics) units[m] = WEATHER_UNITS[m];
  return { items: Array.from(byTs.values()), units };
}

export async function fetchSummary(params: {
  metrics?: string[];
  from?: string;
  to?: string;
  gatewayId?: string | string[];
  sensorId?: string | string[];
}): Promise<SummaryResult> {
  if (isTestMode()) {
    // Build a pool of synthetic measurements similar to backend
    const total = 500;
    let items = Array.from({ length: total }, (_, i) => generateRecord(i));
    // Filter by metric(s)
    if (params.metrics && params.metrics.length) {
      const set = new Set(params.metrics);
      items = items.filter((r) => set.has(r.metric));
    }
    // Filter by gatewayId
    if (params.gatewayId) {
      const gids = Array.isArray(params.gatewayId) ? params.gatewayId : [params.gatewayId];
      items = items.filter((r) => r.gateway && gids.includes(r.gateway.id));
    }
    // Filter by sensorId
    if (params.sensorId) {
      const sids = Array.isArray(params.sensorId) ? params.sensorId : [params.sensorId];
      items = items.filter((r) => r.sensor && sids.includes(r.sensor.id));
    }
    // Date filters
    const fromDt = params.from ? new Date(params.from) : undefined;
    const toDt = params.to ? new Date(params.to) : undefined;
    if (fromDt) items = items.filter((r) => new Date(r.timestamp) >= fromDt);
    if (toDt) items = items.filter((r) => new Date(r.timestamp) <= toDt);

    // Aggregate per sensor
    const agg: Record<string, {
      id: string; name: string; kind: DeviceRef['kind']; lastTs?: string; metrics: Record<string, { nums: number[]; unit?: string }>
    }> = {};
    for (const r of items) {
      const dev = r.sensor;
      const key = dev.id;
      const a = agg[key] || (agg[key] = { id: dev.id, name: dev.name, kind: dev.kind, lastTs: undefined, metrics: {} });
      if (!a.lastTs || r.timestamp > a.lastTs) a.lastTs = r.timestamp;
      const m = r.metric;
      const bucket = a.metrics[m] || (a.metrics[m] = { nums: [], unit: r.unit });
      bucket.nums.push(r.value);
      bucket.unit = bucket.unit || r.unit;
    }

    const out: DeviceSummaryItem[] = [];
    for (const key of Object.keys(agg)) {
      const entry = agg[key];
      const metricsOut: Record<string, { avg: number; max: number; unit: string }> = {};
      for (const [m, b] of Object.entries(entry.metrics)) {
        const nums = b.nums;
        if (nums.length) {
          const avg = nums.reduce((s, n) => s + n, 0) / nums.length;
          const mx = Math.max(...nums);
          metricsOut[m] = { avg: Math.round(avg * 100) / 100, max: Math.round(mx * 100) / 100, unit: b.unit || '' };
        }
      }
      // Deterministic battery 10..100
      const seed = Math.abs(hashCode(entry.id)) % 91 + 10;
      const battery = seed <= 100 ? seed : 100;
      out.push({ id: entry.id, name: entry.name, kind: entry.kind, lastTs: entry.lastTs, battery, metrics: metricsOut });
    }

    // Also aggregate gateways: group sensors to gateways in blocks of 5, skipping every 7th sensor similar to backend mock
    const gatewaysAgg: Record<string, { id: string; name: string; kind: DeviceRef['kind']; lastTs?: string; metrics: Record<string, { nums: number[]; unit?: string }> }> = {};
    for (const sId of Object.keys(agg)) {
      const snNum = parseInt(sId.split('-')[1] || '0', 10);
      if (!Number.isFinite(snNum) || snNum % 7 === 0) continue; // sensors without gateway
      const gwIdx = Math.floor((snNum - 1) / 5) + 1;
      const gid = `gw-${gwIdx}`;
      const g = gatewaysAgg[gid] || (gatewaysAgg[gid] = { id: gid, name: `Gateway ${gwIdx}`, kind: 'gateway', lastTs: undefined, metrics: {} });
      const sensorEntry = agg[sId];
      if (sensorEntry.lastTs && (!g.lastTs || sensorEntry.lastTs > g.lastTs)) g.lastTs = sensorEntry.lastTs;
      for (const [m, b] of Object.entries(sensorEntry.metrics)) {
        const gm = g.metrics[m] || (g.metrics[m] = { nums: [], unit: b.unit });
        gm.nums.push(...b.nums);
        gm.unit = gm.unit || b.unit;
      }
    }
    for (const gid of Object.keys(gatewaysAgg)) {
      const g = gatewaysAgg[gid];
      const metricsOut: Record<string, { avg: number; max: number; unit: string }> = {};
      for (const [m, b] of Object.entries(g.metrics)) {
        const nums = b.nums;
        if (nums.length) {
          const avg = nums.reduce((s, n) => s + n, 0) / nums.length;
          const mx = Math.max(...nums);
          metricsOut[m] = { avg: Math.round(avg * 100) / 100, max: Math.round(mx * 100) / 100, unit: b.unit || '' };
        }
      }
      const seed = Math.abs(hashCode(gid)) % 91 + 10;
      const battery = seed <= 100 ? seed : 100;
      out.push({ id: g.id, name: g.name, kind: g.kind, lastTs: g.lastTs, battery, metrics: metricsOut });
    }

    return {
      items: out,
      total: out.length,
      metrics: params.metrics || METRICS,
      from: params.from,
      to: params.to,
    };
  }

  const resp = await api.get(`${API_BASE}/summary`, {
    params: {
      metric: params.metrics,
      from: params.from,
      to: params.to,
      gatewayId: params.gatewayId,
      sensorId: params.sensorId,
    },
    paramsSerializer: {
      serialize: (p: Record<string, unknown>) => {
        const usp = new URLSearchParams();
        for (const [k, v] of Object.entries(p)) {
          if (v == null) continue;
          if (Array.isArray(v)) {
            for (const it of v) usp.append(k, String(it));
          } else {
            usp.append(k, String(v));
          }
        }
        return usp.toString();
      },
    },
  });
  return resp.data as SummaryResult;
}

// simple string hash like Java's
function hashCode(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return h >>> 0;
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeDevice(id: string, name: string, kind: DeviceRef['kind']): DeviceRef {
  return { id, name, kind };
}

// Generate a single measurement record (used in FE test mode)
export function generateRecord(index: number): MeasurementRecord {
  const gateway = makeDevice(`gw-${(index % 3) + 1}`, `Gateway ${(index % 3) + 1}`, 'gateway');
  const sensor = makeDevice(`sn-${(index % 8) + 1}`, `Sensor ${(index % 8) + 1}`, 'sensor');
  const metric = pick(METRICS);
  const now = new Date();
  const ts = new Date(now.getTime() - index * 60_000); // one minute apart
  const isHumidity = metric.startsWith('humidity');
  const value = metric === 'temperature'
    ? randomBetween(18, 28)
    : isHumidity
      ? randomBetween(35, 75)
      : metric === 'pressure'
        ? randomBetween(980, 1040)
        : metric === 'battery'
          ? randomBetween(20, 100)
          : metric === 'spannung'
            ? randomBetween(3.0, 4.2)
            : randomBetween(0, 100);
  const unit = metric === 'temperature' ? '°C' : isHumidity ? '%' : metric === 'pressure' ? 'hPa' : metric === 'spannung' ? 'V' : '';

  return {
    id: `m-${index}`,
    timestamp: ts.toISOString(),
    gateway,
    sensor,
    metric,
    value: parseFloat(value.toFixed(2)),
    unit,
  };
}
// Simple seeded random number generator
class SeededRandom {
  private seed: number;
  constructor(seed: number) {
    this.seed = seed;
  }
  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }
  uniform(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
}

function generateSeriesDummy(params: {
  metrics?: string[];
  bucket?: 'minute' | 'hour';
  points?: number;
  resolution?: string;
  from?: string;
  to?: string;
  gatewayId?: string | string[];
  sensorId?: string | string[];
  stats?: string;
}): SeriesResult {
  const metrics = params.metrics || METRICS;
  const points = params.points || 120;
  const bucket = params.bucket || 'minute';
  const resolution = params.resolution;
  const from = params.from;
  const to = params.to;
  const deviceKey = Array.isArray(params.sensorId)
    ? (params.sensorId[0] || 'sensor')
    : (params.sensorId || (Array.isArray(params.gatewayId) ? (params.gatewayId[0] || 'gateway') : (params.gatewayId || 'global')));

  const toDt = to ? new Date(to) : new Date();
  let step = bucket === 'hour' ? 60 * 60 * 1000 : 60 * 1000; // ms
  let fromDt = from ? new Date(from) : new Date(toDt.getTime() - step * points);

  // Adjust step if resolution provided
  if (resolution) {
    if (resolution.endsWith('m') && !resolution.endsWith('mo')) {
      const mins = parseInt(resolution.slice(0, -1)) || 1;
      step = mins * 60 * 1000;
    } else if (resolution.endsWith('h')) {
      const hrs = parseInt(resolution.slice(0, -1)) || 1;
      step = hrs * 60 * 60 * 1000;
    } else if (resolution.endsWith('d')) {
      const days = parseInt(resolution.slice(0, -1)) || 1;
      step = days * 24 * 60 * 60 * 1000;
    }
    // Recalculate fromDt if needed
    fromDt = from ? new Date(from) : new Date(toDt.getTime() - step * points);
  }

  const series: Array<Record<string, unknown>> = [];
  const prevClose: Record<string, number> = {};

  for (let i = 0; i < points; i++) {
    const bt = new Date(fromDt.getTime() + i * step);
    const row: Record<string, unknown> = { timestamp: bt.toISOString() };
    const baseSeed = Math.floor(bt.getTime() / 1000);

    for (const m of metrics) {
      if (!METRICS.includes(m)) continue;
      // Seed per device + metric so each device has its own distinct curve
      const baseMetricSeed = m.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
      const deviceSeed = hashCode(`${deviceKey}::${m}`);
      const seed = baseSeed + baseMetricSeed + deviceSeed;
      const rnd = new SeededRandom(seed);

      // Establish realistic centers and spreads; apply deterministic per-device baseline shifts
      let center: number, spread: number, minV: number, maxV: number;
      if (m === 'temperature') {
        center = 23.0; spread = 1.6; minV = 15; maxV = 30;
      } else if (m.startsWith('humidity')) {
        center = 55.0; spread = 5.5; minV = 30; maxV = 80;
      } else if (m === 'pressure') {
        center = 1010.0; spread = 6.0; minV = 980; maxV = 1040;
      } else if (m === 'battery') {
        center = 70.0; spread = 4.0; minV = 20; maxV = 100;
      } else { // spannung
        center = 3.7; spread = 0.12; minV = 3.2; maxV = 4.2;
      }
      // Stronger baseline shift per sensor/device so lines separate more clearly
      const baselineShift = ((hashCode(`base:${deviceKey}:${m}`) % 2000) / 2000 - 0.5) * (spread * 1.6);
      center += baselineShift;

      if (params.stats === 'quartiles') {
        // Generate a synthetic distribution and derive quartiles/whiskers
        const s1 = center + rnd.uniform(-spread * 0.8, spread * 0.8);
        const s2 = center + rnd.uniform(-spread * 0.6, spread * 0.6);
        const s3 = center + rnd.uniform(-spread * 0.4, spread * 0.4);
        const s4 = center + rnd.uniform(-spread * 0.6, spread * 0.6);
        const s5 = center + rnd.uniform(-spread * 0.8, spread * 0.8);
        const arr = [s1, s2, s3, s4, s5].map((v) => Math.max(minV, Math.min(maxV, v))).sort((a, b) => a - b);
        const low = arr[0];
        const q1 = arr[1];
        const median = arr[2];
        const q3 = arr[3];
        const high = arr[4];
        row[m] = {
          low: Math.round(low * 100) / 100,
          q1: Math.round(q1 * 100) / 100,
          median: Math.round(median * 100) / 100,
          q3: Math.round(q3 * 100) / 100,
          high: Math.round(high * 100) / 100,
        } as unknown as SeriesPoint[string];
        prevClose[m] = median;
      } else if (params.stats === 'minmaxavg') {
        // Provide min/max/avg per point (simulated around a random walk)
        const prev = prevClose[m] ?? (center + rnd.uniform(-spread * 0.12, spread * 0.12));
        const delta = rnd.uniform(-spread * 0.06, spread * 0.06);
        let c = prev + delta + rnd.uniform(-spread * 0.02, spread * 0.02);
        c += (center - prev) * 0.025;
        c = Math.max(minV, Math.min(maxV, c));
        const o = prev;
        const wick = Math.abs(rnd.uniform(0, spread * 0.03));
        const hi = Math.max(o, c) + wick;
        const lo = Math.min(o, c) - wick;
        prevClose[m] = c;
        row[m] = {
          low: Math.round(Math.max(minV, lo) * 100) / 100,
          median: Math.round(c * 100) / 100,
          high: Math.round(Math.min(maxV, hi) * 100) / 100,
        } as unknown as SeriesPoint[string];
      } else if (params.stats === 'avg') {
        const prev = prevClose[m] ?? (center + rnd.uniform(-spread * 0.12, spread * 0.12));
        const delta = rnd.uniform(-spread * 0.06, spread * 0.06);
        let c = prev + delta + rnd.uniform(-spread * 0.02, spread * 0.02);
        c += (center - prev) * 0.025;
        c = Math.max(minV, Math.min(maxV, c));
        prevClose[m] = c;
        row[m] = {
          median: Math.round(c * 100) / 100,
        } as unknown as SeriesPoint[string];
      } else {
        // Gentle random walk: small delta around previous close
        const prev = prevClose[m] ?? (center + rnd.uniform(-spread * 0.12, spread * 0.12));
        const delta = rnd.uniform(-spread * 0.06, spread * 0.06);
        let c = prev + delta + rnd.uniform(-spread * 0.02, spread * 0.02);
        // Soft mean reversion: nudge towards center
        c += (center - prev) * 0.025;
        // Clamp to realistic range
        c = Math.max(minV, Math.min(maxV, c));
        const o = prev;
        const wick = Math.abs(rnd.uniform(0, spread * 0.03));
        const h = Math.max(o, c) + wick;
        const l = Math.min(o, c) - wick;
        prevClose[m] = c;

        row[m] = {
          low: Math.round(Math.max(minV, l) * 100) / 100,
          q1: Math.round(Math.min(maxV, Math.min(o, c)) * 100) / 100,
          median: Math.round(c * 100) / 100,
          q3: Math.round(Math.min(maxV, Math.max(o, c)) * 100) / 100,
          high: Math.round(Math.min(maxV, h) * 100) / 100,
        } as unknown as SeriesPoint[string];
      }
    }
    series.push(row);
  }

  return {
    items: series as SeriesPoint[],
    bucket,
    from: fromDt.toISOString(),
    to: toDt.toISOString(),
    metrics,
  };
}

// Simulated backend
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

export async function fetchMeasurements(query: DataQuery): Promise<PagedResult<PivotedMeasurementRecord>> {
  if (isTestMode()) {
    // In test mode, generate pivoted data structure
    const allItems = Array.from({ length: 100 }, (_, i) => {
      const record = generateRecord(i);
      // Convert to pivoted format
      const pivoted: PivotedMeasurementRecord = {
        id: record.id,
        timestamp: record.timestamp,
        gateway: record.gateway!,
        sensor: record.sensor,
        temperature: record.metric === 'temperature' ? record.value : null,
        humidity_general: record.metric.startsWith('humidity') ? record.value : null,
        battery: record.metric === 'battery' ? record.value : null,
        spannung: record.metric === 'spannung' ? record.value : null,
      };
      return pivoted;
    });
    
    let filtered = allItems;
    if (query.gatewayId) {
      const gids = Array.isArray(query.gatewayId) ? query.gatewayId : [query.gatewayId];
      filtered = filtered.filter(r => r.gateway && gids.includes(r.gateway.id));
    }
    if (query.sensorId) {
      const sids = Array.isArray(query.sensorId) ? query.sensorId : [query.sensorId];
      filtered = filtered.filter(r => sids.includes(r.sensor.id));
    }
    if (query.from) {
      const from = new Date(query.from);
      filtered = filtered.filter(r => new Date(r.timestamp) >= from);
    }
    if (query.to) {
      const to = new Date(query.to);
      filtered = filtered.filter(r => new Date(r.timestamp) <= to);
    }
    const total = filtered.length;
    const page = query.page || 1;
    const pageSize = query.pageSize || 10;
    const start = (page - 1) * pageSize;
    const items = filtered.slice(start, start + pageSize);
    return Promise.resolve({
      items,
      total,
      page,
      pageSize,
    });
  }
  const resp = await api.get(`${API_BASE}/measurements`, {
    params: query,
    paramsSerializer: {
      serialize: (p: Record<string, unknown>) => {
        const usp = new URLSearchParams();
        for (const [k, v] of Object.entries(p || {})) {
          if (v == null) continue;
          if (Array.isArray(v)) {
            for (const it of v) usp.append(k, String(it));
          } else {
            usp.append(k, String(v));
          }
        }
        return usp.toString();
      },
    },
  });
  return resp.data as PagedResult<PivotedMeasurementRecord>;
}

export async function listMetrics(): Promise<string[]> {
  if (isTestMode()) {
    return Promise.resolve(METRICS);
  }
  try {
    const resp = await api.get(`${API_BASE}/metrics/ids`);
    return resp.data as string[];
  } catch {
    return METRICS;
  }
}

// Rich metric definitions (id, unit, translation key)
export async function listMetricDefs(): Promise<MetricDef[]> {
  if (isTestMode()) {
    const unitFor: Record<string, string> = {
      temperature: '°C',
      'humidity.general': '%',
      battery: '%',
    };
    return METRICS.map((id) => ({ id, unit: unitFor[id] || '', tKey: `metrics.${id}` }));
  }
  try {
    const resp = await api.get(`${API_BASE}/metrics`);
    return resp.data as MetricDef[];
  } catch {
    // Fallback: derive minimal defs from local METRICS
    const unitFor: Record<string, string> = {
      temperature: '°C',
      'humidity.general': '%',
      battery: '%',
    };
    return METRICS.map((id) => ({ id, unit: unitFor[id] || '', tKey: `metrics.${id}` }));
  }
}

export async function fetchSeries(params: {
  metrics?: string[];
  bucket?: 'minute' | 'hour';
  points?: number;
  resolution?: string; // e.g., '30m', '2h', '7d'
  from?: string;
  to?: string;
  gatewayId?: string | string[];
  sensorId?: string | string[];
  stats?: string; // optional: 'quartiles' for boxplot, 'ohlc' default
}): Promise<SeriesResult> {
  if (isTestMode()) {
    return generateSeriesDummy(params);
  }
  const resp = await api.get(`${API_BASE}/measurements/series`, {
    params: {
      metric: params.metrics,
      bucket: params.bucket,
      points: params.points,
      resolution: params.resolution,
      from: params.from,
      to: params.to,
      gatewayId: params.gatewayId,
      sensorId: params.sensorId,
      stats: params.stats,
    },
    paramsSerializer: {
      // Serialize arrays as repeated keys without [] or indexes
      serialize: (p: Record<string, unknown>) => {
        const usp = new URLSearchParams();
        const entries = Object.entries(p ?? {});
        for (const [key, value] of entries) {
          if (value == null) continue;
          if (Array.isArray(value)) {
            for (const v of value) {
              if (v != null) usp.append(key, String(v));
            }
          } else {
            usp.append(key, String(value));
          }
        }
        return usp.toString();
      },
    },
  });
  return resp.data as SeriesResult;
}

export async function searchGateways(q: string, limit = 2000): Promise<DeviceRef[]> {
  if (isTestMode()) {
    const all = Array.from({ length: 50 }, (_, i) => makeDevice(`gw-${i + 1}`, `Gateway ${i + 1}`, 'gateway'));
    const query = (q || '').toLowerCase();
    const filtered = query
      ? all.filter(d => d.id.toLowerCase().includes(query) || d.name.toLowerCase().includes(query))
      : all;
    return filtered.slice(0, limit);
  }
  try {
    const resp = await api.get(`${API_BASE}/gateways/search`, { params: { q, limit } });
    return (resp.data?.items ?? []) as DeviceRef[];
  } catch (error) {
    let errorMessage = 'Gateways konnten nicht geladen werden.';
    
    if (axios.isAxiosError(error)) {
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = 'Backend-Server nicht erreichbar. Bitte starten Sie den Backend-Server (python run.py im timber_track_iot_be Ordner).';
      } else if (error.response?.status === 404) {
        errorMessage = 'Gateway-Suche-Endpunkt nicht gefunden.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Interner Serverfehler. Bitte versuchen Sie es später erneut.';
      }
    }
    
    // Fehler werfen, damit die UI ihn behandeln kann
    throw new Error(errorMessage);
  }
}

export async function searchSensors(q: string, limit = 2000, gatewayId?: string): Promise<DeviceRef[]> {
  if (isTestMode()) {
    const all = Array.from({ length: 100 }, (_, i) => makeDevice(`sn-${i + 1}`, `Sensor ${i + 1}`, 'sensor'));
    const query = (q || '').toLowerCase();
    const filtered = query
      ? all.filter(d => d.id.toLowerCase().includes(query) || d.name.toLowerCase().includes(query))
      : all;
    // If gatewayId is provided, filter sensors by gateway (in test mode, just return all)
    return filtered.slice(0, limit);
  }
  try {
    const params: Record<string, unknown> = { q, limit };
    if (gatewayId) {
      params.gatewayId = gatewayId;
    }
    const resp = await api.get(`${API_BASE}/sensors/search`, { params });
    return (resp.data?.items ?? []) as DeviceRef[];
  } catch (error) {
    // Benutzerfreundliche Fehlermeldung erstellen
    let errorMessage = 'Sensoren konnten nicht geladen werden.';
    
    if (axios.isAxiosError(error)) {
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = 'Backend-Server nicht erreichbar. Bitte stellen Sie sicher, dass der Backend-Server auf http://localhost:5001 läuft.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Sensor-Suche-Endpunkt nicht gefunden. Überprüfen Sie die Backend-API.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Interner Serverfehler. Bitte versuchen Sie es später erneut.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentifizierung erforderlich. Bitte melden Sie sich an.';
      }
    }
    
    // Fehler werfen, damit die UI ihn behandeln kann
    throw new Error(errorMessage);
  }
}

export async function fetchDevicesDirectory(): Promise<DevicesDirectory> {
  if (isTestMode()) {
    const gateways = Array.from({ length: 5 }, (_, i) => makeDevice(`gw-${i + 1}`, `Gateway ${i + 1}`, 'gateway'));
    const sensors = Array.from({ length: 20 }, (_, i) => makeDevice(`sn-${i + 1}`, `Sensor ${i + 1}`, 'sensor'));
    const mapping: Record<string, string[]> = {};
    sensors.forEach((s, i) => {
      const gwIdx = Math.floor(i / 4) + 1;
      const gid = `gw-${gwIdx}`;
      if (!mapping[gid]) mapping[gid] = [];
      mapping[gid].push(s.id);
    });
    const sensorsWithoutGateway: string[] = [];
    return Promise.resolve({
      gateways,
      sensors,
      mapping,
      sensorsWithoutGateway,
    });
  } else {
    const resp = await api.get(`${API_BASE}/devices/directory`);
    const data = resp.data as DevicesDirectory;
    // Basic shape validation
    return {
      gateways: Array.isArray(data.gateways) ? data.gateways : [],
      sensors: Array.isArray(data.sensors) ? data.sensors : [],
      mapping: typeof data.mapping === 'object' && data.mapping ? data.mapping : {},
      sensorsWithoutGateway: Array.isArray(data.sensorsWithoutGateway) ? data.sensorsWithoutGateway : [],
    };
  }
}
